import React from 'react';
import { cn } from '../../lib/utils';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant */
  variant?: 'default' | 'elevated' | 'bordered' | 'ghost';
  /** Interactive card (shows hover state) */
  interactive?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base styles - Institutional Editor
          'rounded-xl p-4', // xl = 12px radius from design system
          'transition-all duration-300',
          // Variants - No lines, only color shifts
          {
            // Default - Surface container color (No border)
            'bg-surface-container text-on-surface':
              variant === 'default',
            // Elevated - Surface container highest
            'bg-surface-container-highest':
              variant === 'elevated',
            // Bordered - Ghost Border (outline_variant at 20%)
            'bg-surface-container border border-border-subtle': variant === 'bordered',
            // Ghost - Transparent
            'bg-transparent': variant === 'ghost',
          },
          // Interactive states - Institutional Editor hover
          interactive && [
            'cursor-pointer',
            'hover:-translate-y-0.5',
            'hover:bg-surface-container-highest',
            'active:scale-[0.99]',
          ],
          className
        )}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// Card Header
export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between gap-4',
          'pb-3 mb-3',
          // No-line: use surface_container_low background shift instead of border
          'bg-surface-container-low',
          'rounded-t-xl -mx-4 -mt-4 px-4 pt-4',
          className
        )}
        {...props}
      />
    );
  }
);

CardHeader.displayName = 'CardHeader';

// Card Title
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Comp = 'h3', ...props }, ref) => {
    return (
      <Comp
        ref={ref}
        className={cn(
          'text-base font-semibold text-text-primary',
          'font-display',
          className
        )}
        {...props}
      />
    );
  }
);

CardTitle.displayName = 'CardTitle';

// Card Description
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <p
        ref={ref}
        className={cn('text-sm text-text-secondary', className)}
        {...props}
      />
    );
  }
);

CardDescription.displayName = 'CardDescription';

// Card Content
export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('', className)} {...props} />;
  }
);

CardContent.displayName = 'CardContent';

// Card Footer
export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const CardFooter = React.forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3',
          'pt-3 mt-3',
          // No-line: use color shift instead of border
          'bg-surface-container-low',
          'rounded-b-xl -mx-4 -mb-4 px-4 pb-4',
          className
        )}
        {...props}
      />
    );
  }
);

CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
